import styled from 'styled-components';
import { Margin, Padding, StyledTypographyProps, TypographyWeight } from './types';

export const getFontWeight = (weight: TypographyWeight | number) => {
    switch (weight) {
        case 'bold':
            return 600;
        case 'semi-bold':
            return 500;
        case 'regular':
            return 400;
        default:
            return weight;
    }
};

export const getMargin = (margin: Margin) => {
    switch (typeof margin) {
        case 'number':
            return `margin: ${margin}px;`;
        case 'string':
            return `margin: ${margin};`;
        case 'object':
            // eslint-disable-next-line no-case-declarations
            const { top, bottom, left, right } = margin || {};
            // eslint-disable-next-line no-case-declarations
            let css = ``;
            if (top) css += `margin-top: ${top}px;`;
            if (bottom) css += `margin-bottom: ${bottom}px;`;
            if (left) css += `margin-left: ${left}px;`;
            if (right) css += `margin-right: ${right}px;`;
            return css;
        default:
            return 'margin: 0px;';
    }
};

export const getPadding = (padding: Padding) => {
    switch (typeof padding) {
        case 'number':
            return `padding: ${padding}px;`;
        case 'string':
            return `padding: ${padding};`;
        case 'object':
            // eslint-disable-next-line no-case-declarations
            const { top, bottom, left, right } = padding || {};
            // eslint-disable-next-line no-case-declarations
            let css = ``;
            if (top) css += `padding-top: ${top}px !important;`;
            if (bottom) css += `padding-bottom: ${bottom}px !important;`;
            if (left) css += `padding-left: ${left}px !important;`;
            if (right) css += `padding-right: ${right}px !important;`;
            return css;
        default:
            return 'padding: 0px;';
    }
};

const spreadBaseStyles = ({
    align,
    color,
    fontStyle,
    lineHeight,
    overflow,
    size,
    transform,
    weight,
    whitespace,
    margin,
    padding,
    displayType,
}: StyledTypographyProps): string => {
    return `
        margin: 0;
        padding: 0;
        ${padding ? `${getPadding(padding)}` : ''}
        ${margin ? `${getMargin(margin)}` : ''}
        ${size ? `font-size: ${`${size}px`};` : ''}
        ${lineHeight ? `line-height: ${lineHeight || '1'};` : ''}
        ${color ? `color: ${color || 'inherit'};` : ''}
        ${weight ? `font-weight: ${getFontWeight(weight)};` : ''}
        ${transform ? `text-transform: ${transform || 'unset'};` : ''}
        ${whitespace ? `whitespace: ${whitespace};` : ''}
        ${overflow ? `text-overflow: ${overflow};` : ''}
        ${displayType ? `display: ${displayType};` : ''}
        ${fontStyle ? `font-style: ${fontStyle};` : ''}
        ${align ? `text-align: ${align};` : ''}
    `;
};

export const StyledH1 = styled.h1<StyledTypographyProps>`
    font-size: 48px;
    line-height: 64px;
    ${(props) => spreadBaseStyles(props)}
`;

export const StyledH2 = styled.h2<StyledTypographyProps>`
    font-size: 36px;
    line-height: 50px;
    ${(props) => spreadBaseStyles(props)}
`;

export const StyledH3 = styled.h3<StyledTypographyProps>`
    font-size: 28px;
    line-height: 40px;
    ${(props) => spreadBaseStyles(props)}
`;

export const StyledH4 = styled.h4<StyledTypographyProps>`
    font-size: 20px;
    line-height: 28px;
    ${(props) => spreadBaseStyles(props)}
`;

export const StyledSubHead = styled.h6<StyledTypographyProps>`
    font-size: 16px;
    line-height: 24px;
    ${(props) => spreadBaseStyles(props)}
`;

export const StyledBody = styled.p<StyledTypographyProps>`
    font-size: 14px;
    line-height: 20px;
    ${(props) => spreadBaseStyles(props)}
`;

export const StyledSmall = styled.span<StyledTypographyProps>`
    font-size: 12px;
    line-height: 16px;
    ${(props) => spreadBaseStyles(props)}
`;

export const StyledTiny = styled.span<StyledTypographyProps>`
    font-size: 10px;
    line-height: 12px;
    font-weight: 600;
    ${(props) => spreadBaseStyles(props)}
`;
